import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import ScoreBadge, { getScoreConfig } from "@/components/ScoreBadge";
import {
  BarChart2, Search, RefreshCw, LogOut, Settings,
  TrendingUp, Globe, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, Loader2, ToggleLeft, ToggleRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const axCfg = { withCredentials: true };

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card-base p-5 flex items-center gap-4" data-testid={`stat-${label}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-extrabold font-heading text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  const styles = {
    risque_faible: "bg-green-100 text-green-700",
    vigilance: "bg-orange-100 text-orange-700",
    risque_eleve: "bg-red-100 text-red-700",
  };
  const labels = { risque_faible: "Risque faible", vigilance: "Vigilance", risque_eleve: "Risque élevé" };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[level] || "bg-slate-100 text-slate-600"}`}>
      {labels[level] || level}
    </span>
  );
}

const TABS = ["analyses", "signaux"];

export default function AdminDashboardPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analyses");
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [signals, setSignals] = useState([]);
  const [searchDomain, setSearchDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(null);
  const [updatingSignal, setUpdatingSignal] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 20;

  const fetchStats = useCallback(async () => {
    const r = await axios.get(`${API}/admin/stats`, axCfg);
    setStats(r.data);
  }, []);

  const fetchAnalyses = useCallback(async (domain = "", skp = 0) => {
    setLoading(true);
    const params = { limit: LIMIT, skip: skp };
    if (domain) params.domain = domain;
    const r = await axios.get(`${API}/admin/analyses`, { ...axCfg, params });
    setAnalyses(r.data.analyses);
    setTotal(r.data.total);
    setLoading(false);
  }, []);

  const fetchSignals = useCallback(async () => {
    const r = await axios.get(`${API}/admin/signaux`, axCfg);
    setSignals(r.data);
  }, []);

  useEffect(() => {
    fetchStats().catch(() => {});
    fetchAnalyses().catch(() => setLoading(false));
    fetchSignals().catch(() => {});
  }, [fetchStats, fetchAnalyses, fetchSignals]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const handleRescan = async (id) => {
    setRescanning(id);
    try {
      await axios.post(`${API}/admin/analyses/${id}/rescan`, {}, axCfg);
      await fetchAnalyses(searchDomain, skip);
      await fetchStats();
    } finally {
      setRescanning(null);
    }
  };

  const handleSignalToggle = async (key, current) => {
    setUpdatingSignal(key);
    try {
      await axios.put(`${API}/admin/signaux/${key}`, { active: !current }, axCfg);
      await fetchSignals();
    } finally {
      setUpdatingSignal(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    fetchAnalyses(searchDomain, 0);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Seo title="Tableau de bord admin" path="/admin/dashboard" noindex />
      {/* Admin header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40" data-testid="admin-header">
        <div className="container-app">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/"><Logo size="sm" /></Link>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider hidden sm:block">Administration</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                Voir le site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                data-testid="admin-logout"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-extrabold font-heading text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble des analyses et gestion des signaux</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={BarChart2} label="Total analyses" value={stats.total_analyses} color="bg-blue-100 text-blue-600" />
            <StatCard icon={Globe} label="Domaines uniques" value={stats.unique_domains} color="bg-purple-100 text-purple-600" />
            <StatCard icon={TrendingUp} label="Score moyen" value={stats.avg_score} color="bg-orange-100 text-orange-600" />
            <div className="card-base p-5" data-testid="stat-distribution">
              <p className="text-xs text-slate-500 mb-2">Distribution des risques</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-green-500 h-full rounded-full" style={{ width: stats.total_analyses ? `${(stats.risk_distribution.risque_faible / stats.total_analyses) * 100}%` : "0%" }} />
                  </div>
                  <span className="text-xs text-green-600 w-6 text-right">{stats.risk_distribution.risque_faible}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-orange-500 h-full rounded-full" style={{ width: stats.total_analyses ? `${(stats.risk_distribution.vigilance / stats.total_analyses) * 100}%` : "0%" }} />
                  </div>
                  <span className="text-xs text-orange-600 w-6 text-right">{stats.risk_distribution.vigilance}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-red-500 h-full rounded-full" style={{ width: stats.total_analyses ? `${(stats.risk_distribution.risque_eleve / stats.total_analyses) * 100}%` : "0%" }} />
                  </div>
                  <span className="text-xs text-red-600 w-6 text-right">{stats.risk_distribution.risque_eleve}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
          {[
            { id: "analyses", label: "Analyses", icon: BarChart2 },
            { id: "signaux", label: "Signaux", icon: Settings },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {/* Analyses Tab */}
        {tab === "analyses" && (
          <div>
            <form onSubmit={handleSearch} className="flex gap-3 mb-5">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchDomain}
                  onChange={e => setSearchDomain(e.target.value)}
                  placeholder="Rechercher un domaine…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  data-testid="admin-search-input"
                />
              </div>
              <button type="submit" className="btn-primary py-2.5 px-4 text-sm">Rechercher</button>
              {searchDomain && (
                <button type="button" onClick={() => { setSearchDomain(""); setSkip(0); fetchAnalyses("", 0); }}
                  className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700 border border-slate-300 rounded-lg bg-white">
                  Effacer
                </button>
              )}
            </form>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>
            ) : (
              <>
                <div className="card-base overflow-hidden" data-testid="analyses-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold text-slate-600">Domaine</th>
                          <th className="text-left px-4 py-3 font-semibold text-slate-600">Score</th>
                          <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden sm:table-cell">Niveau</th>
                          <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Date</th>
                          <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {analyses.length === 0 ? (
                          <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucune analyse trouvée</td></tr>
                        ) : analyses.map(a => (
                          <tr key={a.id} className="hover:bg-slate-50 transition-colors" data-testid={`analysis-row-${a.id}`}>
                            <td className="px-4 py-3">
                              <a href={`/analyse/${a.domain}`} target="_blank" rel="noopener noreferrer"
                                className="font-medium text-[#1E3A8A] hover:underline font-mono text-xs">{a.domain}</a>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-bold" style={{ color: getScoreConfig(a.score).color }}>{a.score}</span>
                              <span className="text-slate-400 text-xs">/100</span>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <RiskBadge level={a.risk_level} />
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">{formatDate(a.scanned_at)}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleRescan(a.id)}
                                disabled={rescanning === a.id}
                                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-700 disabled:opacity-50 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors border border-slate-200"
                                data-testid={`rescan-btn-${a.id}`}
                              >
                                {rescanning === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                Rescanner
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
                  <span>{total} analyse(s) au total</span>
                  <div className="flex gap-2">
                    <button disabled={skip === 0} onClick={() => { const ns = Math.max(0, skip - LIMIT); setSkip(ns); fetchAnalyses(searchDomain, ns); }}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 bg-white">
                      Précédent
                    </button>
                    <button disabled={skip + LIMIT >= total} onClick={() => { const ns = skip + LIMIT; setSkip(ns); fetchAnalyses(searchDomain, ns); }}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 bg-white">
                      Suivant
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Signals Tab */}
        {tab === "signaux" && (
          <div className="space-y-3" data-testid="signals-panel">
            <p className="text-sm text-slate-500 mb-4">Gérez l'activation des signaux utilisés dans le moteur de scoring.</p>
            {signals.map(signal => (
              <div key={signal.key} className="card-base p-5 flex items-start gap-4" data-testid={`signal-row-${signal.key}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-slate-800 text-sm">{signal.label_fr}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{signal.category}</span>
                    <span className="text-xs text-slate-400">Poids : {signal.default_weight}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{signal.description_fr}</p>
                </div>
                <button
                  onClick={() => handleSignalToggle(signal.key, signal.active)}
                  disabled={updatingSignal === signal.key}
                  className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border transition-colors flex-shrink-0 ${
                    signal.active
                      ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                  }`}
                  data-testid={`signal-toggle-${signal.key}`}
                >
                  {updatingSignal === signal.key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : signal.active ? (
                    <ToggleRight className="w-3.5 h-3.5" />
                  ) : (
                    <ToggleLeft className="w-3.5 h-3.5" />
                  )}
                  {signal.active ? "Actif" : "Désactivé"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
